import { isGranted, isSuperadmin } from 'common/helpers.ts';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link, NavLink } from 'react-router-dom';
import { ILogout, ISettings, IUser } from 'ui/component/Icons.tsx';
import { HeaderMenu } from '#admin/component/HeaderMenu.tsx';
import { LanguageSwitcher } from '#admin/component/LanguageSwitcher.tsx';
import { NotificationBell } from '#admin/component/NotificationBell.tsx';
import { SupportModeBanner } from '#admin/component/SupportModeBanner.tsx';
import { useEnv } from '#admin/hook/useEnv.tsx';
import { useMe } from '#admin/hook/useMe.tsx';
import { actions } from '#admin/reducer/app.ts';

export const Header = () => {
  const { me } = useMe();
  const env = useEnv();
  const { SUPERADMIN_GROUP_ID } = env;
  const { t } = useTranslation();

  return (
    <>
    <SupportModeBanner />
    <header className="fixed top-0 left-0 z-40 flex h-[65px] w-screen justify-center bg-blue py-3">
      <nav className="container flex px-8">
        <Link className="contents" to={'/'}>
          <img className="max-h-[42px]" height="42px" width="167px" src="/atlas-path-nav-logo.svg" alt="Atlas Path Logo" />
        </Link>
        <section className="flex w-full items-center justify-center gap-8 text-lg">
          <NavLink className="header-menu-item" to={'/home'}>
            {t('navigation.overview')}
          </NavLink>
          <NavLink className="header-menu-item" to={'/shipments'}>
            {t('navigation.shipments')}
          </NavLink>
          <NavLink className="header-menu-item" to={'/legs'}>
            {t('navigation.legs')}
          </NavLink>
          {isGranted(me, 'ViewFleet', `${SUPERADMIN_GROUP_ID}/*`) && (
            <NavLink className="header-menu-item" to={'/fleet'}>
              {t('navigation.fleetManagement')}
            </NavLink>
          )}
          {/*
          {isGranted(me, 'ViewTrackers', `${SUPERADMIN_GROUP_ID}/*`) && (
            <NavLink className="header-menu-item" to={'/trackers'}>
              {t('navigation.trackers')}
            </NavLink>
          )}
          */}
          {isGranted(me, 'ViewAlerts', `${SUPERADMIN_GROUP_ID}/*`) && (
            <NavLink className="header-menu-item" to={'/alerts'}>
              {t('navigation.alerts')}
            </NavLink>
          )}
          {isGranted(me, 'ViewActivities', `${SUPERADMIN_GROUP_ID}/*`) && (
            <NavLink className="header-menu-item" to={'/activities'}>
              {t('navigation.activity')}
            </NavLink>
          )}
          {isGranted(me, 'ViewDevelopers', `${SUPERADMIN_GROUP_ID}/*`) && <DeveloperMenu me={me} env={env} />}
          <UserMenu me={me} />
        </section>
        <div className="flex items-center gap-4">
          <NotificationBell />
          <LoginMenu me={me} />
          <LanguageSwitcher />
        </div>
      </nav>
    </header>
    </>
  );
};

type MenuProps = { me: any; env?: any };

const UserMenu = ({ me }: MenuProps) => {
  const { t } = useTranslation();
  return (
    <HeaderMenu title={t('navigation.users')}>
      <Link className="flex flex-row items-center gap-4 hover:opacity-85" to={'/users'}>
        <span>{t('navigation.users')}</span>
      </Link>
      <Link className="flex flex-row items-center gap-4 hover:opacity-85" to={'/groups'}>
        <span>{t('navigation.groups')}</span>
      </Link>
      <Link className="flex flex-row items-center gap-4 hover:opacity-85" to={'/permissions'}>
        <span>{t('navigation.permissions')}</span>
      </Link>

      {isSuperadmin(me) && (
        <Link className="flex flex-row items-center gap-4 hover:opacity-85" to={'/roles'}>
          <span>{t('navigation.roles')}</span>
        </Link>
      )}
      {isSuperadmin(me) && (
        <Link className="flex flex-row items-center gap-4 hover:opacity-85" to={'/support-access'}>
          <span>{t('navigation.supportAccess')}</span>
        </Link>
      )}
    </HeaderMenu>
  );
};

const DeveloperMenu = ({ me, env }: MenuProps) => {
  const { t } = useTranslation();
  return (
    <HeaderMenu title={t('navigation.developers')}>
      {isGranted(me, 'ViewAPIReference', `${env.SUPERADMIN_GROUP_ID}/*`) && (
        <Link className="flex flex-row items-center gap-4 hover:opacity-85" to={new URL('/docs', import.meta.env.VITE_API_URL).href} target="_blank">
          {t('navigation.apiReference')}
        </Link>
      )}
      {isGranted(me, 'ViewApiKeys', `${env.SUPERADMIN_GROUP_ID}/*`) && (
        <Link className="flex flex-row items-center gap-4 hover:opacity-85" to={'/api-keys'}>
          {t('navigation.apiKeys')}
        </Link>
      )}
      {isGranted(me, 'ViewIntegrations', `${env.SUPERADMIN_GROUP_ID}/*`) && (
        <Link className="flex flex-row items-center gap-4 hover:opacity-85" to={'/integrations'}>
          {t('navigation.integrations')}
        </Link>
      )}
    </HeaderMenu>
  );
};

const LoginMenu = ({ me }: MenuProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const logout = () => dispatch(actions.logout());

  return (
    <HeaderMenu title={me.name}>
      <Link className="flex flex-row items-center gap-4 hover:opacity-85" to={`/user/${me.id}`}>
        <IUser size={24} />
        <span>{t('navigation.profile')}</span>
      </Link>
      <Link className="flex flex-row items-center gap-4 hover:opacity-85" to={'/settings'}>
        <ISettings size={24} />
        <span>{t('navigation.settings')}</span>
      </Link>
      <Link className="flex flex-row items-center gap-4 hover:opacity-85" to={'#'} onClick={logout}>
        <ILogout size={24} />
        <span>{t('navigation.logout')}</span>
      </Link>
    </HeaderMenu>
  );
};
